import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    title: "WhatsApp / Telefoon",
    content: ["06 23 86 17 46"],
    href: "https://wa.me/31623861746",
  },
  {
    icon: Mail,
    title: "E-mail",
    content: ["umeyr@ug-detailing.com"],
    href: "mailto:umeyr@ug-detailing.com",
  },
  {
    icon: Clock,
    title: "Beschikbaarheid",
    content: ["Op afspraak", "Neem gerust contact op!"],
  },
];

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Bericht verzonden!");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Er is iets misgegaan. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-ug-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-primary text-sm font-bold tracking-widest uppercase">
              Contact
            </span>
            <h1 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tighter text-white mt-4">
              Neem Contact Op
            </h1>
            <p className="text-gray-400 text-lg mt-6">
              Heeft u vragen of wilt u een afspraak maken? We helpen u graag verder.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-ug-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-5 space-y-8">
              {contactInfo.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 p-6 bg-ug-paper border border-white/5"
                >
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-white font-heading font-bold uppercase tracking-wide">
                      {item.title}
                    </h3>
                    {item.content.map((line, idx) => (
                      <p key={idx} className="text-gray-400 mt-1">
                        {item.href ? (
                          <a
                            href={item.href}
                            className="hover:text-primary transition-colors duration-300"
                            data-testid={`contact-${item.title.toLowerCase()}-link`}
                          >
                            {line}
                          </a>
                        ) : (
                          line
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              ))}

              {/* WhatsApp CTA */}
              <div className="aspect-video bg-ug-paper border border-white/5 overflow-hidden flex flex-col items-center justify-center p-8 text-center">
                <Phone className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-heading font-bold text-white mb-2">Direct Contact?</h3>
                <p className="text-gray-400 mb-4">Stuur een WhatsApp bericht voor snelle reactie</p>
                <a
                  href="https://wa.me/31623861746"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 uppercase tracking-wider text-sm transition-colors duration-300"
                >
                  WhatsApp Openen
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="bg-ug-paper border border-white/5 p-12 text-center"
                >
                  <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-8">
                    <Check className="w-10 h-10 text-primary" />
                  </div>
                  <h2 className="text-2xl font-heading font-black uppercase tracking-tighter text-white mb-4">
                    Bericht Verzonden!
                  </h2>
                  <p className="text-gray-400 mb-8">
                    Bedankt voor uw bericht. We nemen zo snel mogelijk contact met u op.
                  </p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
                    }}
                    className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-8 py-4"
                  >
                    Nieuw Bericht
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-ug-paper border border-white/5 p-8 space-y-6"
                >
                  <div className="flex items-center mb-4">
                    <Send className="w-5 h-5 text-primary mr-3" />
                    <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                      Stuur een Bericht
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">Naam *</Label>
                      <Input
                        id="name"
                        data-testid="contact-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                        placeholder="Uw naam"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">Telefoonnummer</Label>
                      <Input
                        id="phone"
                        data-testid="contact-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                        placeholder="06 12345678"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">E-mailadres *</Label>
                    <Input
                      id="email"
                      data-testid="contact-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                      placeholder="uw@email.nl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-white">Onderwerp *</Label>
                    <Input
                      id="subject"
                      data-testid="contact-subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                      placeholder="Waar gaat uw vraag over?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Bericht *</Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      className="bg-ug-black border-white/10 text-white rounded-none min-h-[150px] placeholder:text-gray-600"
                      placeholder="Uw bericht..."
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-contact"
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold py-4 transition-colors duration-300 disabled:opacity-50"
                  >
                    {loading ? "Bezig met verzenden..." : "Verzenden"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
